import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import { TReportActiveUsersByPeriodData, ReportPeriod } from '@src/types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { ReportGroupBy } from '@src/types/reports';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import {
  getActiveUsersByPeriod,
  exportActiveUsersByPeriod,
} from '@src/services/reports';
import { downloadFile } from '@lib/utils';
import { toast } from 'react-toastify';
import {
  getChartColor,
  useReportChartData,
} from '@src/hooks/useReportChartData';
import { useProducts } from '@src/hooks/useProducts';
import { buildCommonPayloadWithGroupingAndProducts } from '@src/utils/buildCommonPayload';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportActiveUsersByPeriodData;
    color?: string;
    name?: string;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col items-center min-w-[135px] gap-y-1"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] mb-1">
          {payload[0]?.payload?.period}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="text-center">
            <span className="text-white text-xs">{entry.name}: </span>
            <span
              className="text-xl font-semibold"
              style={{ color: entry.color }}
            >
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const ActiveUsersByPeriod: FC = () => {
  const { auth } = useAuthStore();
  const [data, setData] = useState<TReportActiveUsersByPeriodData[]>([]);
  const [summary, setSummary] = useState<
    | {
        active_users: number;
        active_users_previous: number;
        active_users_previous_year: number;
      }
    | undefined
  >(undefined);

  const { products } = useProducts();

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.TODAY,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  const { columns, displayItems, tableData } =
    useReportTableColumns<TReportActiveUsersByPeriodData>({
      data,
      selectedItems: ['active_users'],
      getAccessField: (item) => item.period,
      getMainDataField: (item) => item.active_users || 0,
      getPreviousPeriodField: (item) => item.active_users_previous || 0,
      getPreviousYearField: (item) => item.active_users_previous_year || 0,
      compareToPreviousPeriod: filters.compareToPreviousPeriod,
      compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
      columnTitleFormatter: (key) => {
        if (key === 'active_users') return 'ACTIVE USERS';
        if (key === 'active_users (Previous Period)')
          return 'ACTIVE USERS (Previous Period)';
        if (key === 'active_users (Previous Year)')
          return 'ACTIVE USERS (Previous Year)';

        return key;
      },
      includeTotalsRow: true,
      summaryRow: summary,
    });

  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.period,
    getMainDataField: (item, _) => item.active_users || 0,
    getPreviousPeriodField: (item, _) => item.active_users_previous || 0,
    getPreviousYearField: (item, _) => item.active_users_previous_year || 0,
  });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildCommonPayloadWithGroupingAndProducts(filters);
      const response = await getActiveUsersByPeriod(payload);

      setData(response.data);
      setSummary(response.summary);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to generate report');
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload = buildCommonPayloadWithGroupingAndProducts(filters);
      const response = await exportActiveUsersByPeriod(payload);

      downloadFile(response.data, 'active-users-by-period');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Active Users by Period
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showProductsFilter
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          productOptions={products}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          selectedProducts={filters.selectedProducts}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onProductsChange={filters.setSelectedProducts}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="line" wrapperStyle={{ fontSize: '14px' }} />
              <Line
                activeDot={{ r: 6 }}
                dataKey="active_users"
                dot={{ fill: getChartColor(0), r: 4 }}
                name="Active Users"
                stroke={getChartColor(0)}
                strokeWidth={2}
                type="monotone"
              />
              {filters.compareToPreviousPeriod && (
                <Line
                  activeDot={{ r: 6 }}
                  dataKey="active_users_previous_period"
                  dot={{ fill: getChartColor(1), r: 4 }}
                  name="Previous Period"
                  stroke={getChartColor(1)}
                  strokeWidth={2}
                  type="monotone"
                />
              )}
              {filters.compareToSamePeriodPreviousYear && (
                <Line
                  activeDot={{ r: 6 }}
                  dataKey="active_users_previous_year"
                  dot={{ fill: getChartColor(2), r: 4 }}
                  name="Previous Year"
                  stroke={getChartColor(2)}
                  strokeWidth={2}
                  type="monotone"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
